"use client";

import { useQuery } from "@apollo/client";
import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
	RelatedDiyProjectsDocument,
	type RelatedDiyProjectsQuery,
	type RelatedDiyProjectsQueryVariables,
} from "@pimcore";
import type { PanelVisuProps } from "@tosize/panel-visualisation";
import { formatLocale, getConnection, getThumbnail } from "@tosize/ui";
import {
	type Dispatch,
	type FC,
	type PropsWithChildren,
	type SetStateAction,
	useMemo,
	useState,
} from "react";
import type { LangParams } from "~/app/[lang]/layout";
import { MediaContext } from "~/components/media/Provider";
import { MediaDialog } from "./Dialog";

type ImageVariantType =
	| {
			type: "youtube" | "vimeo";
			videoId: string;
			src?: never;
			alternativeSrc?: never;
	  }
	| {
			type: "default" | "project";
			videoId?: never;
			src: string;
			alternativeSrc?: string;
	  };

export type ImageProps = ImageVariantType & {
	alt: string;
	url?: string;
	priority?: boolean;
	description?: string;
};

export interface SectionProps {
	id: string;
	icon: IconProp;
	title: string;
	items: ImageProps[];
}

export interface MediaInterface extends Props {
	sortedSections: SectionProps[];

	dialogType?: string;
	setDialogType: Dispatch<SetStateAction<string | undefined>>;

	overviewSections: string[];
	setOverviewSections: Dispatch<SetStateAction<string[]>>;

	detailSection?: string;
	setDetailSection: Dispatch<SetStateAction<string | undefined>>;

	detailIndex?: number;
	setDetailIndex: Dispatch<SetStateAction<number | undefined>>;

	panelData: PanelVisuProps | undefined;
	setPanelData: Dispatch<SetStateAction<PanelVisuProps | undefined>>;

	onMediaChange: (props: OnChangeProps) => void;
	fetchMoreProjects: (index: number) => Promise<void>;
}

type OnChangeProps = { type?: string; id?: string; index?: number };

interface Props extends LangParams {
	overviewMessage: string;
	backMessage: string;
	closeMessage: string;
	viewMessage?: string;
	projectMessage?: string;
	sections: SectionProps[];
	sku?: string;
}

export const MediaProvider: FC<PropsWithChildren<Props>> = ({
	children,
	sections: defaultSections,
	...props
}) => {
	const [dialogType, setDialogType] = useState<MediaInterface["dialogType"]>();
	const [detailSection, setDetailSection] = useState<MediaInterface["detailSection"]>();
	const [detailIndex, setDetailIndex] = useState<MediaInterface["detailIndex"]>();

	const [overviewSections, setOverviewSections] = useState<MediaInterface["overviewSections"]>([]);
	const [panelData, setPanelData] = useState<MediaInterface["panelData"]>();

	const filteredSections = defaultSections.filter((section) => !!section.items.length);
	const projectsSection = defaultSections.find((section) => section.id === "projects");
	const defaultLimit = 3;
	const maxLimit = 20;

	const { data, fetchMore } = useQuery<RelatedDiyProjectsQuery, RelatedDiyProjectsQueryVariables>(
		RelatedDiyProjectsDocument,
		{
			variables: {
				defaultLanguage: formatLocale(props.lang, "language_COUNTRY"),
				sku: props.sku!,
				first: defaultLimit,
			},
			skip: !(props.sku && projectsSection),
			context: { clientName: "pim" },
		},
	);

	const fetchMoreProjects = async (index: number) => {
		const indexLimit = Math.max(index + 3, defaultLimit);

		if (indexLimit > defaultLimit && indexLimit < maxLimit) {
			await fetchMore({
				variables: { first: indexLimit },
				updateQuery: (prev, { fetchMoreResult }) => ({ ...prev, ...fetchMoreResult }),
			});
		}
	};

	const sections = useMemo(() => {
		const diyProjects = getConnection(data?.relatedDiyProjects).filter(
			(project) => !!project.images.length,
		);

		if (!(projectsSection && diyProjects.length)) return filteredSections;

		return [
			...filteredSections,
			{
				...projectsSection,
				items: diyProjects.map((project) => ({
					alternativeSrc: getThumbnail(project.images.at(0)!, props.lang, "watermark_1280x720"),
					src: getThumbnail(project.images.at(0)!, props.lang, "watermark_scale_by_width_1280"),
					alt: project.title ?? "",
					type: "project" as const,
					description: project.metaDescription ?? undefined,
					url: project.url ?? undefined,
				})),
			},
		];
	}, [data?.relatedDiyProjects, filteredSections, projectsSection, props.lang]);

	const sortedSections = useMemo(() => {
		return [...sections].sort((a, b) => {
			if (a.id === "images") return -1;
			if (b.id === "images") return 1;
			if (a.id === "projects") return -1;
			if (b.id === "projects") return 1;

			return 0;
		});
	}, [sections]);

	const onMediaChange = ({ type, id, index }: OnChangeProps) => {
		setDialogType(type);
		setDetailSection(id);
		setDetailIndex(index);

		if (type === "overview") {
			setOverviewSections([]);
		}
	};

	return (
		<MediaContext.Provider
			value={{
				dialogType,
				setDialogType,

				overviewSections,
				setOverviewSections,

				detailSection,
				setDetailSection,

				detailIndex,
				setDetailIndex,

				panelData,
				setPanelData,

				onMediaChange,
				sections,
				sortedSections,

				fetchMoreProjects,

				...props,
			}}
		>
			{children}

			<MediaDialog />
		</MediaContext.Provider>
	);
};
